/**
 * Notification Service
 *
 * API client for the current user's in-app notifications, shown in the
 * console's notification bell.
 * `GET/POST /api/v1/notifications*`
 */

import { api } from "@/lib/api";
import type { PaginatedResponse } from "@/types/payment";
import type {
  NotificationListParams,
  ServerNotification,
  UnreadNotificationCount,
} from "@/types/notification";

export const notificationService = {
  /**
   * List the current user's notifications, newest first.
   * `GET /api/v1/notifications`
   */
  getNotifications: async (
    params?: NotificationListParams,
  ): Promise<PaginatedResponse<ServerNotification>> => {
    const result = await api.getList<ServerNotification>("/notifications", {
      params,
    });
    return {
      data: result.data,
      totalCount: result.pagination.totalItems,
      page: result.pagination.currentPage,
      pageSize: result.pagination.itemsPerPage,
      totalPages: result.pagination.totalPages,
    };
  },

  /**
   * How many of the current user's notifications are unread.
   * `GET /api/v1/notifications/unread-count`
   */
  getUnreadCount: (): Promise<UnreadNotificationCount> => {
    return api.get<UnreadNotificationCount>("/notifications/unread-count");
  },

  /**
   * Mark one notification as read.
   * `POST /api/v1/notifications/{id}/read`
   */
  markAsRead: (id: string): Promise<void> => {
    return api.post(`/notifications/${id}/read`);
  },

  /**
   * Mark every unread notification of the current user as read.
   * `POST /api/v1/notifications/read-all`
   */
  markAllAsRead: (): Promise<void> => {
    return api.post("/notifications/read-all");
  },
};
